import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { debugContext } from '@/lib/debugContext';
import { Bell, AlertTriangle, Siren } from 'lucide-react';

/**
 * Pulsante di emergenza per la Debug Console che può essere attivato con una combinazione di tasti
 * o visualizzato sempre in modalità di sviluppo.
 * 
 * Questo componente è pensato per essere accessibile anche quando il resto dell'UI non risponde,
 * per poter sempre accedere alla Debug Console in situazioni critiche.
 */
export default function EmergencyDebugButton() {
  const [isVisible, setIsVisible] = useState(false);
  
  // Rileva automaticamente se siamo in ambiente di sviluppo
  const isDevelopment = import.meta.env.MODE === 'development';
  
  // Nel caso il pulsante sia nascosto, mostra una combinazione di tasti per attivarlo
  useEffect(() => {
    // DISABILITATO: Non mostrare automaticamente in sviluppo per evitare interferenze
    // if (isDevelopment) {
    //   setIsVisible(true);
    //   return;
    // }
    
    // In ambiente di produzione, usa una combinazione di tasti per mostrare/nascondere
    const handleKeyDown = (event: KeyboardEvent) => {
      // Combinazione Ctrl+Shift+D
      if (event.ctrlKey && event.shiftKey && event.key === 'D') {
        setIsVisible(prev => !prev);
        debugContext.logInfo('Toggle EmergencyDebugButton visibility', {
          newState: !isVisible
        }, { component: 'EmergencyDebugButton' });
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isDevelopment, isVisible]);
  
  // Apri la Debug Console quando il pulsante viene premuto
  const openDebugConsole = () => {
    debugContext.logInfo('Debug Console aperta tramite pulsante di emergenza', {
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent
    }, { component: 'EmergencyDebugButton' });
    
    // Prova tutti i metodi possibili per aprire la console
    
    // 1. Accesso diretto allo store attraverso il DOM
    try {
      const rootElement = document.querySelector('#root');
      // @ts-ignore
      if (rootElement && rootElement.__ZUSTAND__) {
        // @ts-ignore
        const state = rootElement.__ZUSTAND__.getState();
        if (state && typeof state.showConsole === 'function') {
          state.showConsole();
          return;
        }
      }
    } catch (error) {
      console.error('Metodo 1 fallito:', error);
    }
    
    // 2. Funzione globale
    try {
      // @ts-ignore
      if (typeof window.showDebugConsole === 'function') {
        // @ts-ignore
        window.showDebugConsole();
        return;
      }
    } catch (error) {
      console.error('Metodo 2 fallito:', error);
    }
    
    // 3. Import diretto
    try {
      const { useDebugConsoleStore } = require('@/stores/debugConsoleStore');
      const store = useDebugConsoleStore.getState();
      if (store && typeof store.showConsole === 'function') {
        store.showConsole();
        return;
      }
    } catch (error) {
      console.error('Metodo 3 fallito:', error);
    }
    
    // 4. Messaggio all'utente se tutto fallisce
    alert('Impossibile aprire la Debug Console. Per favore, controlla la console del browser per i dettagli degli errori.');
  };
  
  if (!isVisible) return null;
  
  return (
    <Button
      variant="outline"
      size="icon"
      className="fixed bottom-4 right-20 z-50 text-primary-foreground bg-primary hover:bg-primary border-blue-300 animate-pulse hover:animate-none"
      onClick={openDebugConsole}
      title="Debug di emergenza"
    >
      <AlertTriangle className="h-5 w-5 text-red-600" />
    </Button>
  );
}
